<main id="main" class="main__offset-header">
  <section class="container">
    <h1>Frequently Asked Questions</h1>
    <div class="row">
      <div class="col-12 col-lg-8">
        <h2>Open Maps For Europe</h2>
        <h3>Who is developing Open Maps For Europe?</h3>
        <p>
          Open Maps For Europe 2 (OME2) is a new project that will develop a new production process and technical
          specification for free-to-use, edge-matched data under a single open licence. Authoritative 1:10 000 scale
          data for 10 countries will be delivered via the user interface built by the award-winning Open Maps For
          Europe Project. The project will also enhance the five existing Open Maps For Europe datasets,
          including the pilot Open Cadastral Map.
        </p>
        <p>
          The project started in January 2023 and will conclude in December 2025.
        </p>
        <p>
          The OME2 project, co-funded by the European Union, is being delivered by a consortium comprising:
          EuroGeographics the not-for-profit membership association for European National Mapping, Cadastral and Land
          Registration Authorities (NMCAs); National Geographic Institute, Belgium; National Institute of Geographic and
          Forest Information, France; Hellenic Cadastre; General Directorate for the Cadastre, Spain; and Cadastre,
          Land Registry and Mapping Agency, The Netherlands
        </p>
        <p>
          It follows the
          <a href="https://eurogeographics.org/open-maps-for-europe" target="_blank">Open Maps For Europe<i
              class="bi bi-box-arrow-up-right ms-1"></i></a>
          project which was coordinated by
          <a href="https://eurogeographics.org" target="_blank">EuroGeographics<i
              class="bi bi-box-arrow-up-right ms-1"></i></a>, the not-for-profit membership association for European
          National Mapping, Cadastral and
          Land Registration Authorities (NMCAs), in partnership with the
          <a href="https://www.ngi.be/website" target="_blank">National Geographic Institute (NGI) Belgium
            <i class="bi bi-box-arrow-up-right ms-1"></i></a>. The Open Maps For Europe project ran until 31 December
          2022 and was co-financed by the Connecting Europe Facility of the European Union.
        </p>
        <p>
          The Open Maps For Europe online service is developed by
          <a href="https://idoxgeospatial.co.uk" target="_blank">Idox Geospatial<i
              class="bi bi-box-arrow-up-right ms-1"></i></a>
          and enables users to discover, view, license and download the open datasets.
        </p>
        <p>
          EuroGeographics is committed to demonstrating how NMCAs contribute to the public good
          through unique international collaboration. This project is delivering:
        </p>
        <ul>
          <li>
            A new production process and technical specification for free-to-use- high-value data from multiple
            European countries under a single open licence.
          </li>
          <li>
            A prototype for harmonised, edge-matched large-scale pan-European datasets covering 10 countries and
            aligned to key EU Policy objectives and UN core geospatial data recommendations.
          </li>
          <li>
            Enhancements to the five existing Open Maps For Europe datasets, including the pilot Open Cadastral Map.
          </li>
          <li>
            An infrastructure, process and tools for members, partners or key stakeholders who wish to use them to
            fund and create pan-European, harmonised, edge matched High Value Data in the future.
          </li>
        </ul>
        <p>
          This activity addresses the challenge of finding, easily accessing and licensing authoritative
          pan-European harmonised edge-matched, large-scale, high-value geospatial data.
        </p>
        <h3>Who are these maps for?</h3>
        <p>
          These maps are for anyone who requires harmonised data from official sources.
        </p>
        <h2>Open Maps For Europe content and coverage</h2>
        <h3>Who is providing the open data for Open Maps For Europe?</h3>
        <p>
          National Mapping, Cadastral and Land Registration Authorities that are members of
          EuroGeographics are providing the data for Open Maps For Europe, except for the
          Pan-European Imagery which is created from data taken from the European Union's Earth
          observation programme Copernicus (Sentinel-2, L1C-L2A).
        </p>
        <h3>How is the Pan-European data created?</h3>
        <p>
          Our datasets are created through a unique production system which is managed by
          EuroGeographics Head office, and the Production Management Team made up of members.
          EuroGeographic's members provide national data to a standard set of specifications for
          EuroRegionalMap, and the data is then harmonised and quality assured. EuroGlobalMap is
          generalised from this dataset.
        </p>
        <p>
          The Open Gazetteer is created from the data within EuroRegionalMap and our licensed
          dataset EuroBoundaryMap, then enhanced and quality assured.
        </p>
        <p>
          The Pan-European imagery dataset is created from the European Union's Earth observation
          programme Copernicus (Sentinel-2, L1C-L2A).
        </p>
        <p>
          The Open Cadastral Map takes INSPIRE open data allowing the user to find out what is
          available in one place. This cadastral map comprises of four data types; Administrative
          Units, Cadastral Parcels (and Cadastral Zones), Buildings (and Building Parts) and
          Addresses. Currently it is a prototype.
        </p>
        <p>
          EuroRegionalMap, the Open Gazetteer and the Pan-European Imagery datasets are managed by
          Germany’s Federal Agency for Cartography and Geodesy (BKG), and EuroGlobalMap is managed
          by the National Institute of Geographic and Forest Information (IGN France). The Open
          Cadastral Map is created through a new production process taking INSPIRE open data
          directly from NMCA services, created by Idox Geospatial, who are a
          sub-contractor on this project.
        </p>
        <p>
          A new production process and technical specification is being created for the production of the
          High-Value Large Scale pan European prototype. This dataset is a multi-themed topographic
          dataset at the scale 1:10 000 aligned to key EU Policy objectives and UN core geospatial data recommendations.
          It is seamless and harmonised authoritative data and is produced by the OME2 project using EuroGeographics’
          members’ national databases.
        </p>
        <p>
          If you would like to know more, please do
          <a routerLink="/contact" target="_blank">contact us</a>.
        </p>
        <p>Coverage is confirmed as follows:</p>
        <ul>
          <li>
            <a routerLink="/datasets/euro-global-map" target="_blank">EuroGlobalMap</a> 1:1 million
            scale open dataset - 55 countries and territories.
          </li>
          <li>
            <a routerLink="/datasets/euro-regional-map" target="_blank">EuroRegionalMap</a>
            1:250,000 scale open dataset – 38 countries and territories from 31 data providers
          </li>
          <li>
            <a routerLink="/datasets/euro-dem" target="_blank">EuroDEM</a> Digital elevation model –
            46 countries and territories from 29 data providers and infilled with SRTM open data
          </li>
          <li>
            <a routerLink="/datasets/open-gazetteer" target="_blank">Open Gazetteer</a> Pan European
            Open Gazetteer – 36 countries from 37 data providers
          </li>
          <li>
            <a routerLink="/datasets/pan-european-imagery" target="_blank">Pan-European Imagery</a>
            Remote sensing data of the European Union's Earth observation programme Copernicus
            (Sentinel-2, L1C-L2A) – Full coverage of geographical Europe
          </li>
          <li>
            <a routerLink="/datasets/cadastral-all" target="_blank">Open Cadastral Map</a>
            15 agencies contributing data. Please visit the individual dataset pages for detailed information of coverage
          </li>
          <li>
            <a routerLink="/datasets/hvlsp" target="_blank">High-Value Large Scale Prototype</a>
            1:10,000 scale open dataset - 10 agencies contributing data across three themes.
          </li>
          <li>
            Please visit the individual dataset pages for detailed information of coverage.
          </li>
        </ul>
        <h3>Why is there no data for my country included?</h3>
        <p>
          We have been engaging with all our members who currently contribute data to our pan
          European datasets. Unfortunately, some are unable to include their data at this time in an
          open dataset. We are continuing to work with our members to increase the coverage for
          future releases of the open datasets.
        </p>
        <h3>
          I downloaded the Open Gazetteer as a dataset but it does not include all the names that
          are in the search function of the interface which is also using the Open Gazetteer, why is
          this?
        </h3>
        <p>
          The Open Gazetteer has been infilled with data from GeoNames, to ensure that the search
          functionality is as comprehensive as possible for end users when using the interface. We
          aim to increase the coverage of the Gazetteer as the project progresses.
        </p>
        <h3>Why is the data styled as it is?</h3>
        <p>
          The cartography and styling on the Open Maps For Europe interface for EuroRegionalMap and
          EuroGlobalMap is based on NGI Belgium cartography and then amended slightly.
        </p>
        <p>
          The styling for the Open Cadastral Map is based on the styles defined by the European
          Location Framework Cadastre (Regional) specification which was developed through the
          European Location Framework project.
        </p>
        <p>
          The styling for the High-Value Large Scale Prototype is based on work completed within the OME2 by
          the project partners.
        </p>
        <h3>Why is there not a lot of larger scale data available?</h3>
        <p>
          The aim of the first Open Maps for Europe project was to make the existing EuroGeographic's pan
          European datasets available as open data and easily accessible by the end user.
        </p>
        <p>
          One of the key outputs of the OME2 project is a prototype of harmonised, edge-matched large-scale
          pan-European datasets covering 10 countries and aligned to key EU Policy objectives and UN core geospatial
          data recommendations. The first release included 3 countries across 2 high value themes, and this will
          be enhanced throughout the duration of the project.
        </p>
        <p>
          At the same time the coverage of the open Cadastral Map will be increased.
        </p>
        <p>
          In addition, end users can access data directly from EuroGeographic's members geoportal.
          You can find out more information on the
          <a href="https://eurogeographics.org/our-members/" target="_blank">EuroGeographics website here</a>
        </p>
        <h3>Can I report an error in the data?</h3>
        <p>
          If you think you have spotted an error in the data, you can
          <a routerLink="/contact" target="_blank">contact us</a> and we will pass the information
          on to the national data provider.
        </p>
        <h3>Where can I find the user documentation?</h3>
        <p>
          The documentation for each dataset is available to download from the individual dataset
          pages, and is also included in the email you receive when requesting access to the data.
        </p>
        <h3>How often is the data updated?</h3>
        <p>
          EuroGlobalMap, EuroRegionalMap, the Open Gazetteer and The Open Cadastral Map are updated annually. The
          Pan-European Imagery is updated every three years, and the EuroDEM is a static dataset. The Open Cadastral Map
          is updated by EuroGeographics every year. The data from the Cadastral Agencies may be updated on a
          different frequency cycle. The last update of the dataset is detailed on the specific dataset pages. The
          High-Value Large Scale Prototype will be updated throughout the OME2 project.
        </p>

        <h2>Using the Open Maps For Europe services and datasets</h2>
        <h3>How do I connect to the services?</h3>
        <p>
          To connect to the services, you must first visit the
          <a routerLink="/access-data" target="_blank">Access Data</a> page and request access to
          the services you wish to use. A token will then be e-mailed to you at the e-mail address
          you have provided.
        </p>
        <h3>What is token-based authentication?</h3>
        <p class="word-wrap">
          For token-based authentication, copy the token provided in the e-mail. Add your token with
          the token parameter. For example:
          https://api.themapcloud.com/maps/wms?service=WMS&request=GetCapabilities&token=your_token_here
        </p>
        <h3>How do I add more services?</h3>
        <p>
          To add more services, simply visit the
          <a routerLink="/access-data" target="_blank">Access Data</a> page and select more
          services. Your token will remain the same and can be applied to the new services.
        </p>
        <div class="alert alert-primary">
          <div class="d-flex align-items-center">
            <i class="bi bi-exclamation-circle-fill" style="font-size: 2rem"></i>
            <p class="mb-0 ms-3">
              Please note that your token is unique to you, please do not share it with others.
            </p>
          </div>
        </div>
        <h3>What format is the dataset available to download as?</h3>
        <p>
          The High-Value Large Scale Prototype dataset as a geopackage. EuroGlobalMap, EuroRegionalMap and the Open Gazetteer can be downloaded as either shapefiles, or geopackage. EuroRegionalMap can also be downloaded as a FileGeoDatabase.
        </p>
        <p>EuroDEM dataset can be downloaded as a Tiff.</p>
        <p>
          The Pan-European Imagery is not available to be downloaded due to the large size of the
          file. We recommend users access this as a service.
        </p>
        <p>
          The Open Cadastral Map is not available to be downloaded due to the large size of the
          files. If you would like to use this map please access this as a service. Alternatively,
          to access the Cadastral Data, please use the links provided which will take you directly
          to the national data.
        </p>
        <h3>Where can I find the metadata?</h3>
        <p>Metadata is provided with the data when you request it.</p>
        <h3>What if I have trouble either downloading or accessing the service?</h3>
        <p>
          If you are downloading EuroRegionalMap in the Shapefile format, please note that the roads feature class
          exceeds the allowed size limit. If you are having difficulties we would recommend you download the dataset as
          GeoPackage.
        </p>
        <p>
          If you are having other issues please check your own systems first and if you continue to have problems please
          <a routerLink="/contact" target="_blank">contact us</a>.
        </p>
        <h3>How will I know if there has been an update to the dataset?</h3>
        <p>
          To receive updates please sign up for our newsletter here
          <a href="https://eurogeographics.us1.list-manage.com/subscribe?u=5c0e0618f3242180bd70b3dd5&id=31bcc5abef"
            target="_blank">Sign up for our newsletter<i class="bi bi-box-arrow-up-right ms-1"></i></a>. You can also
          follow on LinkedIn using the
          <a href="https://www.linkedin.com/search/results/all/?keywords=%23mapsforeurope&origin=GLOBAL_SEARCH_HEADER&sid=mQT" rel="noopener noreferrer"
            target="_blank" role="link">#OpenMapsForEurope<i class="bi bi-box-arrow-up-right ms-1"></i></a>
        </p>
        <h3>Will data provided meet the INSPIRE specifications?</h3>
        <p>The datasets that are provided via Open Maps For Europe are not INSPIRE compliant.</p>
        <p>The Open Cadastral Map takes INSPIRE open data directly from our members.</p>
        <p>
          The thematic structure and the definitions of features and attributes of the High-Value Large-Scale Prototype
          are based on the INSPIRE data specifications, and on the results the UN-GGIM: Europe working group on core
          data specifications3. Core data specifications are built upon INSPIRE data specifications.
         Please see the Data Specifications document for more information.
        </p>
        <h3>Why do I not receive style guides when I download the datasets?</h3>
        <p>
          The datasets are provided for you to style as you wish and do not come with pre-defined
          style guides.
        </p>
        <h3>I have downloaded multiple geopackages of the HVLSP. How can I merge these easily in QGIS?</h3>
        <p>
          You will notice two issues after having downloaded multiple geopackages and imported them in QGIS:
        </p>
        <ul>
          <li>
            Geopackages imported in QGIS will appear as originating from different datasets even if the data share the
            same structure.
          </li>
          <li>
            At the administrative limits between two adjacent geopackages, data intersecting theses boundaries will be
            duplicated. These duplicated objects will have the same geometry and the same attributes, including the same
            value for « objectid ».
          </li>
        </ul>
        <p>
          In order to obtain a clean and merged dataset, several steps are required, using existing QGIS tools developed
          to process data. Some of these steps may be very time consuming and prone to mistakes. IGN France have created
          a How To Guide to support you in this work and automate some of the steps with a new tool. You can find the
          Guide
          <a href="../../assets/els/pdfs/HVLSPMergePackagesUserManual.pdf" download>
             here <i class="bi bi-box-arrow-up-right ms-1"></i>
          </a>
          and the tool
          <a href="../../assets/els/zips/hvlsp_merge_packages.zip" download>
             here <i class="bi bi-box-arrow-up-right ms-1"></i>
          </a>.
        </p>
        <h2>Open Maps For Europe licensing</h2>
        <h3>Which open data licence does Open Maps For Europe use?</h3>
        <p>
          To be able to deliver the Open Maps For Europe datasets, EuroGeographics is using its
          existing
          <button type="button" (click)="openLicenseTermsModal()" class="btn btn-link p-0">
            Open Data Licence
          </button>
          which members have already agreed to. This licence allows users to distribute, remix,
          adapt, and build upon the material in any medium or format, so long as attribution is
          given to the creator. The license allows for commercial use. This licence covers
          EuroGlobalMap, EuroRegionalMap, EuroDEM, the Open Gazetteer and the High Value Large Scale Prototype.
        </p>
        <p>
          EuroDEM is infilled with MERIT data, please see
          <a href="https://hydro.iis.u-tokyo.ac.jp/~yamadai/MERIT_DEM/" target="_blank">
             MERIT DEM: Multi-Error-Removed Improved-Terrain DEM <i class="bi bi-box-arrow-up-right ms-1"></i>
          </a>
          for more information.
        </p>
        <p>
          The Pan-European Imagery is processed by the Federal Agency for Cartography and Geodesy,
          and is made available free of charge under the following terms of use:
          <a href="https://sentinel.esa.int/documents/247904/690755/Sentinel_Data_Legal_Notice" target="_blank">Sentinel
            Data and Service Information<i class="bi bi-box-arrow-up-right ms-1"></i></a>
        </p>
        <p>
          The Open Cadastral Map takes INSPIRE open data from EuroGeographics members. To use the
          national cadastral available via the links provided, please accept the relevant national
          open data licence.
        </p>
        <h3>Is the Open Maps For Europe open data licence similar to a CC-BY licence?</h3>
        <p>Yes, it is similar in that it requires you to provide attribution.</p>
        <h3>What attribution should I use?</h3>
        <p>
          For EuroGlobalMap, EuroRegionalMap, EuroDEM, the Gazetteer and the High-Value Large Scale Prototype
          the attribution statement is:
        </p>
        <p>
          "This dataset includes Intellectual Property from European National Mapping and Cadastral
          Authorities and is licensed on behalf of these by EuroGeographics. Original dataset is
          available for free at https://www.mapsforeurope.org. Terms of the licence available at
          <a href="https://www.mapsforeurope.org/licence" target="_blank">https://www.mapsforeurope.org/licence</a>. All
          attribution statements can be found
          <a href="https://www.mapsforeurope.org/attributions" target="_blank">here</a>."
        </p>
        <p>
          In case the long statement above cannot be used, you may use the following short form:
        </p>
        <p>"&copy; EuroGeographics [insert year]"</p>
        <p>
          All individual attributions from the data providers are included in the documentation for
          each dataset.
        </p>
        <p>For the Pan-European Imagery please use the following attribution:</p>
        <p>
          &copy; European Union, contains Copernicus Sentinel-2 data [year], processed by the
          Federal Agency for Cartography and Geodesy (BKG)
        </p>
        <p>
          The source reference has to be linked with the URL "http://www.bkg.bund.de" for use on a
          website.
        </p>
        <p>
          For the Open Cadastral Map WFS please attribute © EuroGeographics [insert year]. If
          accessing the national cadastral data through the links provided, please refer to the
          relevant national open data licence for attribution information.
        </p>
        <h2>Open Maps For Europe and other open data initiatives</h2>
        <h3>Is this project linked to other European funded projects?</h3>
        <p>
          Open Maps For Europe 2 (OME2) follows and builds upon the outputs of the Open Maps For Europe project.
        </p>
        <h3>Will other datasets be included in the future?</h3>
        <p>
          For the duration of this project, the High-Value Large Scale Prototype and its production process will
          be developed, and delivered through Open Maps For Europe, however the existing datasets will be updated
          where relevant. After the end of this project, EuroGeographics would aim to provide access to any new datasets
          through this interface, as and when applicable.
        </p>
        <h3>Does Open Maps For Europe connect with any other open data initiatives?</h3>
        <p>
          Open Maps For Europe datasets are linked to and discoverable via the
          <a href="https://www.europeandataportal.eu/en" target="_blank">European Data Portal<i
              class="bi bi-box-arrow-up-right ms-1"></i></a>
          as well as the national geoportals of some of EuroGeographics members. This work is
          ongoing throughout the project.
        </p>
        <h3>What is the link to the European Commission's Data Spaces?</h3>
        <p>
          Open Maps for Europe is compliant with the Open Data and PSI directive, particularly
          aiming to facilitate the wider opening and re-use of High Value Datasets – a key enabler
          for creating a single market for data (data spaces) envisaged in European strategy for
          data. The High-Value Large Scale Prototype demonstrates the value of authoritative high-value data from
          official national sources that is harmonised and edge-matched across borders. We fully support the strategy's
          principle that “The value of data lies in its use and re-use”;
        </p>
        <p>
          EuroGeographics' membership is committed to this principle which Open Maps
          for Europe reflects. The Strategy recognises the need for the pan European datasets
          providing the vision of a common European data space which implies an open, but reliable
          (authoritative data) approach to international data flows, based on European values. Open
          Maps for Europe demonstrates how demand for geospatial information in all common data spaces of
          the European Strategy for Data can be met.
        </p>
      </div>
    </div>
  </section>
</main>
<app-footer showBackToTop="true" target="main"></app-footer>
